/** Dates arrive as ISO strings ("2026-09-19" or "2026-09-19T09:12:00+00:00")
 * and every screen shows them one way: the day as the documents write it,
 * "19 Sep 2026", and a time, when there is one, as "09:12" on the reader's
 * own clock. Three formats had
 * grown up side by side -- the ISO day, the browser's locale string and a
 * hand-built one -- so the same date read three ways on one job. */

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const ISO = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/;

/** A timestamp that says which zone it is in: "Z" or "+02:00" at the end. */
const ZONED = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})$/;

const pad = (n: number): string => String(n).padStart(2, "0");

interface Parts {
  year: number;
  month: number;
  day: number;
  time: string | null;
}

/** The calendar day and wall-clock time as the reader's clock would show
 * them. A timestamp that carries its zone (the server stamps everything in
 * UTC and says so) is converted to the browser's local time, so 23:45 UTC
 * reads as 01:45 the next day in Johannesburg. A date-only or zone-less
 * value is taken exactly as written and never pushed through `Date`, which
 * would read a bare day as UTC midnight and can show the day before. */
function parts(value: string): Parts | null {
  const text = value.trim();
  if (ZONED.test(text)) {
    const local = new Date(text);
    if (!Number.isNaN(local.getTime())) {
      return {
        year: local.getFullYear(),
        month: local.getMonth() + 1,
        day: local.getDate(),
        time: `${pad(local.getHours())}:${pad(local.getMinutes())}`,
      };
    }
  }
  const match = ISO.exec(text);
  if (match === null) {
    return null;
  }
  const [, year, month, day, hours, minutes] = match;
  const monthNumber = Number(month);
  if (monthNumber < 1 || monthNumber > 12) {
    return null;
  }
  return {
    year: Number(year),
    month: monthNumber,
    day: Number(day),
    time: hours === undefined ? null : `${hours}:${minutes}`,
  };
}

/** "2026-09-19" -> "19 Sep 2026". Empty in, a dash out; anything unreadable
 * comes back as it went in rather than as "Invalid Date". */
export function formatDate(value: string | null | undefined): string {
  if (value === null || value === undefined || value === "") {
    return "—";
  }
  const read = parts(value);
  if (read === null) {
    return value;
  }
  return `${read.day} ${MONTHS[read.month - 1]} ${read.year}`;
}

/** "2026-09-19T09:12:00" -> "19 Sep 2026, 09:12"; a bare day has no time. */
export function formatDateTime(value: string | null | undefined): string {
  if (value === null || value === undefined || value === "") {
    return "—";
  }
  const read = parts(value);
  if (read === null) {
    return value;
  }
  const day = `${read.day} ${MONTHS[read.month - 1]} ${read.year}`;
  return read.time === null ? day : `${day}, ${read.time}`;
}

/** "12 Sep" -- the short form a pipeline step or a list row uses. */
export function formatDayMonth(value: string | null | undefined): string {
  if (value === null || value === undefined || value === "") {
    return "—";
  }
  const read = parts(value);
  if (read === null) {
    return value;
  }
  return `${read.day} ${MONTHS[read.month - 1]}`;
}

/** Today on the reader's own calendar, "2026-10-15", for a date field's
 * default. Built from the local year, month and day: `toISOString` is UTC, and
 * between midnight and 02:00 in Johannesburg it names yesterday. */
export function todayLocal(): string {
  const now = new Date();
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}
